import { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { DeliveryActionsDropdown } from '@/_components/delivery-actions-dropdown/delivery-actions-dropdown';
import type { TaxPayerObj } from '@/Components/delivery/cancelDelivery/ModalCancelDelivery';
import ModalCancelDelivery, {
  TAX_PAYER_OPTIONS,
} from '@/Components/delivery/cancelDelivery/ModalCancelDelivery';
import ModalConnectDelivery from '@/Components/delivery/connectDelivery/ModalConnectDelivery';
import type {
  DeliveryAction,
  DeliveryActionTarget,
} from '@/hooks/delivery/useDeliveryAction';
import useDeliveryAction from '@/hooks/delivery/useDeliveryAction';
import {
  GET_MONITOR_DELIVERIES,
  GET_MONITOR_DELIVERY_DETAIL,
} from '../../constants';
import type { MonitorDelivery } from '../../types';
import styles from './delivery-actions.module.scss';
import { queryClient } from '@/_config/lib/react-query/query-client';

type DeliveryActionsSource = Pick<MonitorDelivery, '_id' | 'status'> & {
  deliveryman: Pick<
    NonNullable<MonitorDelivery['deliveryman']>,
    'id' | 'name'
  > | null;
};

interface DeliveryActionsProps {
  delivery: DeliveryActionsSource;
  variant?: 'outline' | 'filled';
  onActionFinished?: (action: DeliveryAction) => void;
}

const toDeliveryActionTarget = ({
  _id,
  status,
  deliveryman,
}: DeliveryActionsSource): DeliveryActionTarget => ({
  _id,
  status,
  entregadorId: deliveryman?.id ?? '',
  nomeEntregador: deliveryman?.name ?? '',
});

export function DeliveryActions({
  delivery,
  variant,
  onActionFinished,
}: DeliveryActionsProps) {
  const [actionSelected, setActionSelected] = useState<DeliveryAction | null>(
    null,
  );

  function handleActionFinished(action: DeliveryAction) {
    setActionSelected(null);
    queryClient.invalidateQueries([GET_MONITOR_DELIVERIES]);
    queryClient.invalidateQueries([GET_MONITOR_DELIVERY_DETAIL, delivery._id]);
    onActionFinished?.(action);
  }

  const {
    actionLoading,
    availableActions,
    cancelDelivery,
    connectDeliveryToMotoboy,
    triggerDeliveryAction,
  } = useDeliveryAction(toDeliveryActionTarget(delivery), handleActionFinished);

  function handleCancelDelivery(
    action: DeliveryAction,
    taxPayers: TaxPayerObj[],
  ) {
    const collectTaxOfPlace = taxPayers.includes(TAX_PAYER_OPTIONS.PLACE);
    const passOnTaxForMotoboy = taxPayers.includes(TAX_PAYER_OPTIONS.MOTOBOY);

    cancelDelivery(action, collectTaxOfPlace, passOnTaxForMotoboy);
  }

  function handleActionDelivery(action: DeliveryAction) {
    switch (action.key) {
      case 'ASSOCIAR':
      case 'CANCELAR':
        setActionSelected(action);
        break;
      default:
        triggerDeliveryAction(action);
    }
  }

  if (isEmpty(availableActions)) {
    return null;
  }

  return (
    <>
      <DeliveryActionsDropdown
        actions={availableActions}
        loading={!!actionLoading}
        onSelect={handleActionDelivery}
        containerClassName={styles.dropdown}
        variant={variant}
        centerText
      />

      {actionSelected?.key === 'CANCELAR' && (
        <ModalCancelDelivery
          isFinishedDelivery={false}
          disabled={actionLoading === 'CANCELAR'}
          loading={actionLoading === 'CANCELAR'}
          opened
          onCancel={taxPayers =>
            handleCancelDelivery(actionSelected, taxPayers)
          }
          onClose={() => setActionSelected(null)}
        />
      )}

      {actionSelected?.key === 'ASSOCIAR' && (
        <ModalConnectDelivery
          disabled={actionLoading === 'ASSOCIAR'}
          loading={actionLoading === 'ASSOCIAR'}
          opened
          onConnect={(motoboyId: string) =>
            connectDeliveryToMotoboy(actionSelected, motoboyId)
          }
          delivery={delivery}
          onClose={() => setActionSelected(null)}
        />
      )}
    </>
  );
}
